import { useState } from 'react';
import { useLoginCustomer } from 'hooks';
import SiteLink from 'components/shared/sitelink';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import cn from 'classnames';
import Breadcrumbs from 'components/layout/breadcrumbs/breadcrumbs';
import { addLocaleToHref } from 'lib/utils/helpers';
import useIsMobile from 'hooks/utils/useIsMobile';
import {
  getSessionCheckoutUrl,
  WEBSHOP_STORES,
  getSelectedSalonNo,
  getStorageCustomerSalonNo,
} from 'lib/shopify';

import styles from './loginSection.module.scss';

const LoginSection = ({ loginpage, locale, secondpartloginpage }) => {
  const {
    customerFormTitle,
    customerFormEmail,
    customerFormPassword,
    forgotPasswordField,
    loginOrCreate,
    rememberMeField,
    salonOwnerFormEmail,
    salonOwnerFormPassword,
    salonOwnerFormTitle,
    signInButton,
  } = loginpage;
  const {
    registerPromptButton,
    registerPromptContent,
    registerPromptTitle,
    signingInMessage,
    breadcrumbsSection,
    loginError,
  } = secondpartloginpage;
  const isMobile = useIsMobile();
  const affiliatedSalonNo = getStorageCustomerSalonNo(locale);
  const selectedSalonNo = getSelectedSalonNo(locale);
  const checkoutUrl = getSessionCheckoutUrl(locale);

  const [loginCustomer, { loading, response, error }] = useLoginCustomer(locale);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tabIndex, setTabIndex] = useState(0);

  const handleSubmit = async e => {
    e.preventDefault();
    await loginCustomer(email, password, affiliatedSalonNo, selectedSalonNo, checkoutUrl);
  };

  return (
    <section className="loginWrapper">
      {breadcrumbsSection && <Breadcrumbs locale={locale} data={breadcrumbsSection.sections[0]} />}
      <h1 className={styles.title}>{loginOrCreate?.text}</h1>
      <div className={styles.container}>
        <div className={styles.leftSide}>
          <Tabs selectedIndex={tabIndex} onSelect={index => setTabIndex(index)}>
            {/* US webshop has no kickbackfee program yet */}
            {locale !== WEBSHOP_STORES.US && (
              <TabList className={styles.customTablist}>
                <Tab className={cn(styles.customTab1, { [styles.selected]: tabIndex === 0 })}>
                  {customerFormTitle?.text}
                </Tab>
                <Tab className={cn(styles.customTab2, { [styles.selected]: tabIndex === 1 })}>
                  {salonOwnerFormTitle?.text}
                </Tab>
              </TabList>
            )}
            <TabPanel>
              <form className={styles.customer} onSubmit={e => handleSubmit(e)}>
                {error && !isMobile && (
                  <div className={styles.errorContainer}>
                    <span role="img" aria-label="error">
                      ⚠️
                    </span>
                    {loginError.text}
                  </div>
                )}

                <div className={styles.labels}>{customerFormEmail?.text}</div>
                <input
                  name="email"
                  type="email"
                  onChange={e => setEmail(e.target.value)}
                  required
                />
                <div className={styles.labels}>{customerFormPassword?.text}</div>
                <input
                  name="password"
                  type="password"
                  required
                  onChange={e => setPassword(e.target.value)}
                />
                <div className={styles.subSection}>
                  <div className={styles.subscription}>
                    <span id="checkbox1">
                      <input name="subscription" type="checkbox" />
                    </span>
                    <span className={styles.labels}>{rememberMeField?.text}</span>
                  </div>
                  <span className={styles.forgotLink}>
                    <SiteLink
                      locale={locale}
                      href={addLocaleToHref('/account/forgot-password/', locale)}
                    >
                      {forgotPasswordField?.text}
                    </SiteLink>
                  </span>
                </div>
                {error && isMobile && (
                  <div className={styles.errorContainer}>
                    <span role="img" aria-label="error">
                      ⚠️
                    </span>
                    {loginError.text}
                  </div>
                )}
                <button
                  type="submit"
                  className={cn({
                    [styles.mobileMarginChange]: Boolean(error) && isMobile,
                  })}
                >
                  {loading || response ? `${signingInMessage?.text}` : `${signInButton?.text}`}
                </button>
              </form>
            </TabPanel>
            <TabPanel>
              <form className={styles.owner} onSubmit={e => handleSubmit(e)}>
                {error && !isMobile && (
                  <div className={styles.errorContainer}>
                    <span role="img" aria-label="error">
                      ⚠️
                    </span>
                    {error}
                  </div>
                )}
                <div className={styles.labels}>{salonOwnerFormEmail?.text}</div>
                <input
                  name="email"
                  type="email"
                  onChange={e => setEmail(e.target.value)}
                  required
                />
                <div className={styles.labels}>{salonOwnerFormPassword?.text}</div>
                <input
                  name="password"
                  type="password"
                  required
                  onChange={e => setPassword(e.target.value)}
                />
                <div className={styles.subSection}>
                  <div className={styles.subscription}>
                    <input name="subscription" type="checkbox" />
                    <span className={styles.labels}>{rememberMeField?.text}</span>
                  </div>
                  <span className={styles.forgotLink}>
                    <SiteLink
                      locale={locale}
                      href={addLocaleToHref('/account/forgot-password/', locale)}
                    >
                      {forgotPasswordField?.text}
                    </SiteLink>
                  </span>
                </div>
                {error && isMobile && (
                  <div className={styles.errorContainer}>
                    <span role="img" aria-label="error">
                      ⚠️
                    </span>
                    {loginError.text}
                  </div>
                )}
                <button
                  type="submit"
                  className={cn({
                    [styles.mobileMarginChange]: Boolean(error) && isMobile,
                  })}
                >
                  {loading || response ? `${signingInMessage?.text}` : `${signInButton?.text}`}
                </button>
              </form>
            </TabPanel>
          </Tabs>
        </div>
        <div className={styles.rightSide}>
          <h2 className={styles.registerPromptTitle}>{registerPromptTitle?.text}</h2>
          <span className={styles.registerContent}>{registerPromptContent?.text}</span>
          <SiteLink locale={locale} href={addLocaleToHref('/account/register/', locale)}>
            <button className={styles.registerButton} type="button">
              {' '}
              {registerPromptButton?.text}
            </button>
          </SiteLink>
        </div>
      </div>
    </section>
  );
};

export default LoginSection;
